import React, { type PropsWithChildren, useCallback, useRef } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type Attributes, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

const getCurrentTime = () => new Date().getTime();

type HoverEventAnalytics = {
	attributes?: Attributes;
	actionSubjectId: string;
};

type Props = {
	minTime: number; // in milliseconds
	eventAnalytics: HoverEventAnalytics;
};

export const HoverTracker = ({ children, eventAnalytics, minTime }: PropsWithChildren<Props>) => {
	const { actionSubjectId, attributes = {} } = eventAnalytics;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const startTime = useRef<number>();

	const onHoverEnd = useCallback(
		(timeTaken: number) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'hover',
				actionSubject: 'mouse',
			});
			fireUIAnalytics(analyticsEvent, actionSubjectId, { ...attributes, timeTaken });
		},
		[actionSubjectId, attributes, createAnalyticsEvent],
	);

	const onMouseEnter = () => {
		startTime.current = getCurrentTime();
	};

	const onMouseLeave = () => {
		if (startTime.current == null) return;
		const timeTaken = getCurrentTime() - startTime.current;

		if (timeTaken > minTime) {
			onHoverEnd(timeTaken);
		}
	};

	return (
		<div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			{children}
		</div>
	);
};
